export const TerminalLocationsMapHook = {
  mounted() {
    const Leaflet = window.Leaflet;
    if (!Leaflet) {
      console.error("Leaflet.js not loaded");
      return;
    }

    // Store Leaflet reference for use in event handlers
    this.Leaflet = Leaflet;

    // Give DOM a moment to render with proper dimensions
    setTimeout(() => {
      try {
        this.initializeMap(Leaflet);
        this.setupEventListeners(Leaflet);
      } catch (error) {
        console.error("Failed to initialize map:", error);
      }
    }, 100);
  },

  initializeMap(Leaflet) {
    const mapView = this.el.dataset.mapView || 'street';
    const showClusters = this.el.dataset.showClusters === 'true';
    const showHeatmap = this.el.dataset.showHeatmap === 'true';

    const defaultLat = 24.4539;
    const defaultLng = 54.3773;
    const defaultZoom = 7;

    // Check if map already exists and remove it
    if (this.map) {
      this.map.remove();
    }

    this.map = Leaflet.map(this.el, { preferCanvas: true }).setView([defaultLat, defaultLng], defaultZoom);

    // Add different tile layers
    this.tileLayers = {
      street: Leaflet.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors'
      }),
      satellite: Leaflet.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '&copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
      }),
      terrain: Leaflet.tileLayer('https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenTopoMap (CC-BY-SA)'
      })
    };

    // Set initial tile layer
    this.currentTileLayer = this.tileLayers['street'];
    this.currentTileLayer.addTo(this.map);
    this.currentMapView = 'street';

    // Initialize marker clusters group
    this.markersGroup = Leaflet.featureGroup().addTo(this.map);
    this.clusterGroup = null;

    // Initialize heatmap layer
    this.heatmapLayer = null;

    // Add zoom and scale controls only
    this.map.addControl(new Leaflet.Control.Zoom({ position: 'topright' }));
    Leaflet.control.scale({ position: 'bottomleft' }).addTo(this.map);

    // Force Leaflet to recalculate dimensions
    setTimeout(() => {
        if (this.map) {
            this.map.invalidateSize();
        }
    }, 250);

    // Load initial device data
    if (this.el.dataset.devices) {
      try {
        const devices = JSON.parse(this.el.dataset.devices);
        this.updateDeviceMarkers(devices, Leaflet, showClusters, showHeatmap);
      } catch (error) {
        console.error("Error parsing device data:", error);
      }
    }
  },

  setupEventListeners(Leaflet) {
    this.handleEvent("update_map_devices", (data) => {
      if (this.map) {
        this.updateDeviceMarkers(data.devices, Leaflet, data.showClusters, data.showHeatmap);
        setTimeout(() => { this.map.invalidateSize(); }, 200);
      }
    });
  },
  
  updated() {
    // With phx-update="ignore" this should rarely fire, but handle defensively
    if (this.map) {
      setTimeout(() => { this.map.invalidateSize(); }, 100);
    }
  },
  
  updateDeviceMarkers(devices, Leaflet, showClusters = false, showHeatmap = false) {
    // Clear existing markers
    if (this.markersGroup) {
      this.map.removeLayer(this.markersGroup);
    }
    if (this.clusterGroup) {
      this.map.removeLayer(this.clusterGroup);
    }
    if (this.heatmapLayer) {
      this.map.removeLayer(this.heatmapLayer);
    }
    
    // Always create fresh marker groups
    this.markersGroup = Leaflet.featureGroup();
    this.clusterGroup = null;
    
    if (showClusters && window.L && window.L.markerClusterGroup) {
      // Use marker clustering if available
      this.clusterGroup = window.L.markerClusterGroup({
        chunkedLoading: true,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        zoomToBoundsOnClick: true
      });
    } else {
      this.markersGroup = Leaflet.featureGroup();
    }
    
    const heatmapPoints = [];
    
    // Group devices by coordinates to handle overlapping markers
    const groupedDevices = {};
    devices.forEach(device => {
      if (device.lat && device.lng) {
        const key = `${device.lat}_${device.lng}`;
        if (!groupedDevices[key]) groupedDevices[key] = [];
        groupedDevices[key].push(device);
      }
    });

    Object.keys(groupedDevices).forEach(key => {
      const locationDevices = groupedDevices[key];
      const device = locationDevices[0]; // Representative for the marker
      const lat = parseFloat(device.lat);
      const lng = parseFloat(device.lng);
      
      if (!isNaN(lat) && !isNaN(lng)) {
        // Create different styled markers based on device status
        // Use the status of the first device for the icon
        const markerIcon = this.createCustomMarker(device.status, Leaflet);
        const marker = Leaflet.marker([lat, lng], { icon: markerIcon });
        
        // Add detailed popup with device information
        // Build function to generate popup content for multiple devices
        const getPopupContent = (index) => {
          const currentDevice = locationDevices[index];
          return this.createPopupContent(currentDevice, index, locationDevices.length);
        };

        // Initialize marker with first device content
        marker.bindPopup(getPopupContent(0), { maxWidth: 300 });

        // Handle navigation and button clicks when popup opens
        marker.on('popupopen', (e) => {
          const popup = e.popup;
          this.bindPopupEvents(popup, locationDevices, 0);

          // Hide border when popup opens
          const markerElement = marker._icon;
          if (markerElement) {
            markerElement.querySelector('.device-marker')?.classList.add('no-border');
          }
        });

        // Show border when popup closes
        marker.on('popupclose', () => {
          const markerElement = marker._icon;
          if (markerElement) {
            markerElement.querySelector('.device-marker')?.classList.remove('no-border');
          }
        });

        // Add to appropriate layer
        if (this.clusterGroup) {
          this.clusterGroup.addLayer(marker);
        } else {
          this.markersGroup.addLayer(marker);
        }
        
        // Add to heatmap data
        locationDevices.forEach(d => {
            heatmapPoints.push([lat, lng, this.getDeviceIntensity(d)]);
        });
      }
    });
    
    // Remove old heatmap if it exists
    if (this.heatmapLayer) {
      this.map.removeLayer(this.heatmapLayer);
      this.heatmapLayer = null;
    }

    // Add heatmap FIRST (so it appears under markers)
    if (showHeatmap) {
      if (window.L && window.L.heatLayer && heatmapPoints.length > 0) {
        try {
          console.log('Creating heatmap with', heatmapPoints.length, 'points');
          this.heatmapLayer = window.L.heatLayer(heatmapPoints, {
            radius: 30,
            blur: 20,
            maxZoom: 18,
            minOpacity: 0.3,
            gradient: {
              0.2: 'blue',
              0.4: 'cyan',
              0.6: 'lime',
              0.8: 'yellow',
              1.0: 'red'
            }
          });
          this.map.addLayer(this.heatmapLayer);
          console.log('Heatmap added to map');
        } catch (error) {
          console.error('Error creating heatmap:', error);
        }
      }
    }

    // Add markers to map (on top of heatmap)
    if (this.clusterGroup) {
      this.map.addLayer(this.clusterGroup);
    } else {
      this.map.addLayer(this.markersGroup);
    }
    
    // Fit map to show all markers if there are any, otherwise keep current view
    const allLayers = this.clusterGroup || this.markersGroup;
    if (allLayers && allLayers.getLayers && allLayers.getLayers().length > 0) {
      try {
        this.map.fitBounds(allLayers.getBounds(), { padding: [20, 20], maxZoom: 15 });
      } catch (error) {
        // Keep current view if fitBounds fails
      }
    }
  },
  
  createCustomMarker(status, Leaflet) {
    const borderColors = {
      'online': '#10B981',
      'offline': '#EF4444',
      'maintenance': '#F59E0B',
      'error': '#DC2626',
      'default': '#6B7280'
    };

    const borderColor = borderColors[status] || borderColors.default;
    const statusAttr = status || 'default';

    return Leaflet.divIcon({
      className: 'device-marker-container',
      html: `
        <div class="device-marker" data-status="${statusAttr}" style="
          display: flex;
          align-items: center;
          justify-content: center;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: white;
          border: 3px solid ${borderColor};
          box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
        ">
          <img src="/images/POS_leaf.png" alt="Device" style="
            width: 100%;
            height: 100%;
            object-fit: contain;
            padding: 4px;
          " />
        </div>
      `,
      iconSize: [46, 46],
      iconAnchor: [23, 23]
    });
  },
  
  createPopupContent(device, index = 0, total = 1) {
    const statusColors = {
      'online': 'text-green-600',
      'offline': 'text-red-600',
      'maintenance': 'text-yellow-600',
      'error': 'text-red-600'
    };
    
    const statusColor = statusColors[device.status] || 'text-gray-600';
    const navHtml = total > 1 ? `
        <div class="flex items-center justify-between bg-blue-50 p-2 mb-2 rounded border border-blue-100">
            <button id="marker-nav-prev" class="text-blue-600 font-bold px-2 hover:bg-blue-100 rounded">&lt;</button>
            <span class="nav-counter text-xs font-semibold text-blue-800">Device ${index + 1} of ${total}</span>
            <button id="marker-nav-next" class="text-blue-600 font-bold px-2 hover:bg-blue-100 rounded">&gt;</button>
        </div>
    ` : '';
    
    return `
      <div class="p-3 max-w-sm device-popup-container" data-index="${index}">
        ${navHtml}
        <div class="flex items-center justify-between mb-2">
          <h4 class="device-serial font-semibold text-gray-900">${device.serial_number || 'Unknown'}</h4>
          <span class="device-status-badge px-2 py-1 text-xs rounded-full bg-gray-100 ${statusColor}">${device.status || 'Unknown'}</span>
        </div>
        <div class="space-y-1 text-sm text-gray-600">
          <p><span class="font-medium">Area:</span> <span class="device-area">${device.area || 'N/A'}</span></p>
          <p><span class="font-medium">Vendor:</span> <span class="device-vendor">${device.vendor || 'N/A'}</span></p>
          <p><span class="font-medium">Model:</span> <span class="device-model">${device.model || 'N/A'}</span></p>
          <p><span class="font-medium">Address:</span> <span class="device-address">${device.address || 'N/A'}</span></p>
          <p><span class="font-medium">Last Update:</span> <span class="device-update">${device.location_timestamp ? new Date(device.location_timestamp).toLocaleString() : 'N/A'}</span></p>
        </div>
        <div class="mt-4 flex flex-wrap gap-2">
          <button class="map-btn-details flex-1 px-3 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 transition shadow-sm">
            View Details
          </button>
          <button class="map-btn-geofence flex-1 px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 transition shadow-sm">
            Geofence
          </button>
        </div>
      </div>
    `;
  },

  bindPopupEvents(popup, devices, initialIndex) {
    const container = popup.getElement();
    if (!container) return;

    // Prevent clicks inside the popup from reaching the map (closing it)
    window.Leaflet.DomEvent.disableClickPropagation(container);

    let currentIndex = initialIndex;

    const updateDisplay = (index) => {
      const device = devices[index];
      const statusColors = {
        'online': 'text-green-600',
        'offline': 'text-red-600',
        'maintenance': 'text-yellow-600',
        'error': 'text-red-600'
      };

      // Update text content without regenerating the whole DOM
      container.querySelector('.device-serial').innerText = device.serial_number || 'Unknown';
      
      const badge = container.querySelector('.device-status-badge');
      badge.innerText = device.status || 'Unknown';
      badge.className = `device-status-badge px-2 py-1 text-xs rounded-full bg-gray-100 ${statusColors[device.status] || 'text-gray-600'}`;
      
      container.querySelector('.device-area').innerText = device.area || 'N/A';
      container.querySelector('.device-vendor').innerText = device.vendor || 'N/A';
      container.querySelector('.device-model').innerText = device.model || 'N/A';
      container.querySelector('.device-address').innerText = device.address || 'N/A';
      container.querySelector('.device-update').innerText = device.location_timestamp ? new Date(device.location_timestamp).toLocaleString() : 'N/A';
      
      if (devices.length > 1) {
        container.querySelector('.nav-counter').innerText = `Device ${index + 1} of ${devices.length}`;
      }
    };

    const nextBtn = container.querySelector('#marker-nav-next');
    const prevBtn = container.querySelector('#marker-nav-prev');
    const detailBtn = container.querySelector('.map-btn-details');
    const geoBtn = container.querySelector('.map-btn-geofence');

    if (nextBtn) {
      nextBtn.onclick = (e) => {
        e.preventDefault();
        currentIndex = (currentIndex + 1) % devices.length;
        updateDisplay(currentIndex);
      };
    }

    if (prevBtn) {
      prevBtn.onclick = (e) => {
        e.preventDefault();
        currentIndex = (currentIndex - 1 + devices.length) % devices.length;
        updateDisplay(currentIndex);
      };
    }

    if (detailBtn) {
      detailBtn.onclick = (e) => {
        e.preventDefault();
        this.pushEvent("view_device_details", { id: devices[currentIndex].id });
      };
    }

    if (geoBtn) {
      geoBtn.onclick = (e) => {
        e.preventDefault();
        this.pushEvent("create_geofence_modal", { id: devices[currentIndex].id });
      };
    }
  },
  
  getDeviceIntensity(device) {
    // Calculate intensity for heatmap based on device activity
    if (device.status === 'online') return 1.0;
    if (device.status === 'maintenance') return 0.7;
    if (device.status === 'offline') return 0.3;
    return 0.1;
  },
  
  changeMapView(view) {
    if (this.currentTileLayer) {
      this.map.removeLayer(this.currentTileLayer);
    }
    
    this.currentTileLayer = this.tileLayers[view] || this.tileLayers.street;
    this.currentTileLayer.addTo(this.map);
  },
  
  toggleClusters(enabled) {
    // Would need to recreate markers with/without clustering
    if (this.el.dataset.devices) {
      const devices = JSON.parse(this.el.dataset.devices);
      this.updateDeviceMarkers(devices, window.Leaflet, enabled, false);
    }
  },
  
  toggleHeatmap(enabled) {
    if (this.heatmapLayer) {
      if (enabled) {
        this.map.addLayer(this.heatmapLayer);
      } else {
        this.map.removeLayer(this.heatmapLayer);
      }
    }
  },
  
  destroyed() {
    if (this.map) {
      this.map.remove();
    }
  }
};

export default TerminalLocationsMapHook;
